import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Wrench } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useTranslation } from "@/hooks/useTranslation";
import { useTheme } from "@/contexts/ThemeContext";
import ConsentAgreement from "@/components/ConsentAgreement";
import BotProtection from "@/components/BotProtection";
import { formatApiError } from "@/utils/apiError";
import { isEmailFilled, requiredFieldsFilled } from "@/utils/formReady";
import { trackLead } from "@/utils/ga4";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function ScheduleServicePage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const [formData, setFormData] = useState({
    firstName: "", lastName: "", email: "", mobilePhone: "",
    preferredTimeToCall: "",
    preferredServiceDate: "",
    vin: "", vehicleType: "", vehicleYear: "", vehicleMake: "", vehicleModel: "",
    problemDescription: ""
  });
  const [consent, setConsent] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const canSubmit =
    consent &&
    isEmailFilled(formData.email) &&
    formData.vin.replace(/\s/g, "").length === 17 &&
    requiredFieldsFilled([
      formData.firstName,
      formData.lastName,
      formData.mobilePhone,
      formData.preferredTimeToCall,
      formData.preferredServiceDate,
      formData.vehicleType,
      formData.vehicleYear,
      formData.vehicleMake,
      formData.vehicleModel,
      formData.problemDescription,
    ]);

  const handleChange = (e) => { setFormData(prev => ({ ...prev, [e.target.name]: e.target.value })); };
  const handleSelectChange = (name, value) => { setFormData(prev => ({ ...prev, [name]: value })); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!consent) { toast.error(t("consent.formRequired")); return; }
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/service-request`, { ...formData, consent, captcha_token: captchaToken });
      trackLead("service");
      toast.success("Service request submitted successfully! We'll contact you soon to confirm your appointment.");
      setFormData({ firstName: "", lastName: "", email: "", mobilePhone: "", preferredTimeToCall: "", preferredServiceDate: "", vin: "", vehicleType: "", vehicleYear: "", vehicleMake: "", vehicleModel: "", problemDescription: "" });
      setConsent(false);
      setCaptchaToken("");
      setTimeout(() => { navigate('/'); }, 2000);
    } catch (error) {
      console.error("Service request submission error:", error);
      toast.error(formatApiError(error, "Failed to submit service request. Please try again."));
    } finally { setSubmitting(false); }
  };

  const inputCls = isDark ? "bg-white/5 border-white/10 text-white" : "bg-white border-gray-300 text-gray-900";
  const selectTriggerCls = isDark ? "bg-white/5 border-white/10 text-white" : "bg-white border-gray-300 text-gray-900";
  const selectContentCls = isDark ? "bg-gray-900 border-white/10" : "bg-white border-gray-200";
  const selectItemCls = isDark ? "text-white hover:bg-white/10" : "text-gray-900 hover:bg-gray-50";
  const labelCls = isDark ? "text-white/80 font-['Manrope'] mb-2" : "text-gray-700 font-['Manrope'] mb-2";
  const stepBadgeCls = isDark ? "bg-blue-500" : "bg-red-600";

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDark ? 'bg-[#050505]' : 'bg-[#FAFAFA]'}`}>
      <Navbar />
      
      <div className="pt-32 pb-20 px-6">
        <div className="max-w-3xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
            <button onClick={() => navigate('/')} className={`inline-flex items-center gap-2 transition-colors mb-6 ${isDark ? 'text-white/60 hover:text-white' : 'text-gray-500 hover:text-gray-900'}`}>
              <ArrowLeft className="w-4 h-4" /><span className="font-['Manrope']">{t('common.back')}</span>
            </button>
            <div className={`inline-flex items-center justify-center w-16 h-16 border mb-6 ${isDark ? 'bg-blue-500/10 border-blue-500/20' : 'bg-red-500/10 border-red-500/20'}`}>
              <Wrench className={`w-8 h-8 ${isDark ? 'text-blue-400' : 'text-red-600'}`} />
            </div>
            <h1 className={`text-4xl md:text-5xl font-['Unbounded'] font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('scheduleService.title')}</h1>
            <p className={`text-xl font-['Manrope'] ${isDark ? 'text-white/60' : 'text-gray-600'}`}>{t('scheduleService.description')}</p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className={`p-8 ${isDark ? 'bg-white/5 backdrop-blur-sm border border-white/10' : 'bg-white border border-gray-200 shadow-sm'}`}>
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Personal Information */}
              <div className="space-y-6">
                <h2 className={`text-2xl font-['Unbounded'] font-bold flex items-center gap-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  <span className={`w-8 h-8 ${stepBadgeCls} flex items-center justify-center text-sm text-white`}>1</span>
                  {t('scheduleService.section1Title')}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><Label htmlFor="firstName" className={labelCls}>{t('financing.firstName')} *</Label><Input id="firstName" name="firstName" value={formData.firstName} onChange={handleChange} required className={inputCls} placeholder="John" /></div>
                  <div><Label htmlFor="lastName" className={labelCls}>{t('financing.lastName')} *</Label><Input id="lastName" name="lastName" value={formData.lastName} onChange={handleChange} required className={inputCls} placeholder="Doe" /></div>
                </div>
                <div><Label htmlFor="email" className={labelCls}>{t('contact.emailLabel')} *</Label><Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} required className={inputCls} placeholder={t('contact.emailPlaceholder')} /></div>
                <div><Label htmlFor="mobilePhone" className={labelCls}>{t('financing.mobileNumber')} *</Label><Input id="mobilePhone" name="mobilePhone" type="tel" value={formData.mobilePhone} onChange={handleChange} required className={inputCls} placeholder={t('contact.phonePlaceholder')} /></div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="preferredTimeToCall" className={labelCls}>{t('scheduleService.preferredTimeOfContact')} *</Label>
                    <Select value={formData.preferredTimeToCall} onValueChange={(value) => handleSelectChange("preferredTimeToCall", value)} required>
                      <SelectTrigger className={selectTriggerCls}><SelectValue placeholder={t('scheduleService.selectTime')} /></SelectTrigger>
                      <SelectContent className={selectContentCls}>
                        <SelectItem value="anytime" className={selectItemCls}>Anytime</SelectItem>
                        <SelectItem value="morning" className={selectItemCls}>Morning</SelectItem>
                        <SelectItem value="midday" className={selectItemCls}>Midday</SelectItem>
                        <SelectItem value="evening" className={selectItemCls}>Evening</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="preferredServiceDate" className={labelCls}>{t('scheduleService.preferredDate')} *</Label>
                    <Input
                      id="preferredServiceDate"
                      name="preferredServiceDate"
                      type="date"
                      value={formData.preferredServiceDate}
                      onChange={handleChange}
                      required
                      className={inputCls}
                    />
                  </div>
                </div>
              </div>

              {/* Vehicle Information */}
              <div className="space-y-6">
                <h2 className={`text-2xl font-['Unbounded'] font-bold flex items-center gap-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  <span className={`w-8 h-8 ${stepBadgeCls} flex items-center justify-center text-sm text-white`}>2</span>
                  {t('scheduleService.section2Title')}
                </h2>
                <div><Label htmlFor="vin" className={labelCls}>{t('financing.vin')} *</Label><Input id="vin" name="vin" value={formData.vin} onChange={handleChange} required minLength="17" maxLength="17" className={`${inputCls} uppercase`} placeholder={t('tradeIn.vinPlaceholder')} /></div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><Label htmlFor="vehicleType" className={labelCls}>{t('scheduleService.vehicleBodyType')} *</Label><Input id="vehicleType" name="vehicleType" value={formData.vehicleType} onChange={handleChange} required className={inputCls} placeholder={t('scheduleService.vehicleBodyPlaceholder')} /></div>
                  <div><Label htmlFor="vehicleYear" className={labelCls}>{t('financing.vehicleYear')} *</Label><Input id="vehicleYear" name="vehicleYear" type="number" value={formData.vehicleYear} onChange={handleChange} required min="1900" max="2030" className={inputCls} placeholder={t('tradeIn.yearPlaceholder')} /></div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><Label htmlFor="vehicleMake" className={labelCls}>{t('financing.vehicleMake')} *</Label><Input id="vehicleMake" name="vehicleMake" value={formData.vehicleMake} onChange={handleChange} required className={inputCls} placeholder={t('tradeIn.makePlaceholder')} /></div>
                  <div><Label htmlFor="vehicleModel" className={labelCls}>{t('financing.vehicleModel')} *</Label><Input id="vehicleModel" name="vehicleModel" value={formData.vehicleModel} onChange={handleChange} required className={inputCls} placeholder={t('tradeIn.modelPlaceholder')} /></div>
                </div>
              </div>

              {/* Service Details */}
              <div className="space-y-6">
                <h2 className={`text-2xl font-['Unbounded'] font-bold flex items-center gap-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  <span className={`w-8 h-8 ${stepBadgeCls} flex items-center justify-center text-sm text-white`}>3</span>
                  {t('scheduleService.section3Title')}
                </h2>
                <div><Label htmlFor="problemDescription" className={labelCls}>{t('scheduleService.problemDescription')} *</Label>
                  <Textarea id="problemDescription" name="problemDescription" value={formData.problemDescription} onChange={handleChange} required rows={4}
                    className={`${inputCls} resize-none`} placeholder={t('scheduleService.problemPlaceholder')} />
                </div>
              </div>

              {/* Consent */}
              <div className={`space-y-4 pt-4 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                <h3 className={`text-lg font-['Unbounded'] font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('scheduleService.consentTitle')}</h3>
                <ConsentAgreement
                  id="consent"
                  checked={consent}
                  onCheckedChange={setConsent}
                  isDark={isDark}
                  checkboxClassName={`mt-1 ${isDark ? '' : 'border-gray-400 data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600'}`}
                />
              </div>

              <BotProtection onToken={setCaptchaToken} isDark={isDark} />

              {/* Submit */}
              <div className="pt-6">
                <Button type="submit" disabled={submitting || !canSubmit}
                  className={`w-full text-white font-['Manrope'] font-semibold py-6 text-lg disabled:opacity-50 disabled:cursor-not-allowed ${isDark ? 'bg-blue-500 hover:bg-blue-600' : 'bg-red-600 hover:bg-red-700'}`}>
                  {submitting ? t('scheduleService.submitting') : t('scheduleService.submitBtn')}
                </Button>
                <p className={`text-center text-sm font-['Manrope'] mt-4 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>{t('scheduleService.successMsg')}</p>
              </div>
            </form>
          </motion.div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
